import { Box, Button, ProgressBar, Typography } from "@outschool/backpack";
import { Trans, useTranslation } from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import { useMutation } from "@outschool/ui-apollo";
import { HeroLayout } from "@outschool/ui-components-classroom";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAnalytics } from "use-analytics";

import {
  LearnerQuizCompleteQuizAttemptMutation,
  LearnerQuizCompleteQuizAttemptMutationVariables,
  LearnerQuizStartQuizAttemptMutation,
  LearnerQuizStartQuizAttemptMutationVariables,
  LearnerQuizUpdateQuizAttemptMutation,
  LearnerQuizUpdateQuizAttemptMutationVariables,
} from "../../generated/graphql";
import Footer from "../../layout/nav/Footer";
import Header from "../../layout/nav/Header";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import { useLearnerAuth } from "../../providers/LearnerAuthProvider";
import { useRandomDidYouKnowFact } from "./decorativeText";
import { QuestionCard } from "./QuestionCard";
import {
  LearnerQuizCompleteQuizAttempt,
  LearnerQuizStartQuizAttempt,
  LearnerQuizUpdateQuizAttempt,
} from "./quizQueries";
import { QuizWriterStartForm } from "./QuizWriterStartForm";
import { QuizQuestion } from "./types";

/** Visible steps on the generating UI; step 10 completes when quiz content arrives. */
const QUIZ_GENERATION_TOTAL_STEPS = 10;
/** After this many `user_log` messages, hold the bar until `result` (final content). */
const QUIZ_GENERATION_PROGRESS_CAP = 9;

/** Same path as {@link QUIZ_WRITER_AGENT_WS_PATH} on the website server. */
const QUIZ_WRITER_WS_PATH = "/sockets/quiz-writer";

export default function Quiz() {
  const { t } = useTranslation("learnerApp\\Quiz");
  const { track } = useAnalytics();
  const { sessionTokenStore } = useLearnerAuth();
  const currentLearner = useCurrentLearner();
  const [topic, setTopic] = useState("");
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<number, number>
  >({});
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [revealedByIndex, setRevealedByIndex] = useState<
    Record<number, boolean>
  >({});
  /** Whether the learner's choice matched the question's correct answer (for attempt persistence). */
  const [answeredCorrectByIndex, setAnsweredCorrectByIndex] = useState<
    Record<number, boolean>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Count of `user_log` messages, capped at {@link QUIZ_GENERATION_PROGRESS_CAP} until the quiz is ready. */
  const [generationLogStepCount, setGenerationLogStepCount] = useState(0);
  const [latestUserLogMessage, setLatestUserLogMessage] = useState<
    string | null
  >(null);
  const pickRandomDidYouKnowFact = useRandomDidYouKnowFact(t);
  const [didYouKnowFact, setDidYouKnowFact] = useState<string>(
    pickRandomDidYouKnowFact(),
  );
  const socketRef = useRef<WebSocket | null>(null);
  /** Set when the server sends `result`, before async work; avoids treating normal post-result close as failure while `loading` is still true. */
  const quizGenerationGotResultRef = useRef(false);
  const quizUidRef = useRef<string | null>(null);
  const completionSentRef = useRef(false);
  const [attemptUid, setAttemptUid] = useState<string | null>(null);

  const [startQuizAttemptMutation] = useMutation<
    LearnerQuizStartQuizAttemptMutation,
    LearnerQuizStartQuizAttemptMutationVariables
  >(LearnerQuizStartQuizAttempt);

  const [updateQuizAttemptMutation] = useMutation<
    LearnerQuizUpdateQuizAttemptMutation,
    LearnerQuizUpdateQuizAttemptMutationVariables
  >(LearnerQuizUpdateQuizAttempt);

  const [completeQuizAttemptMutation] = useMutation<
    LearnerQuizCompleteQuizAttemptMutation,
    LearnerQuizCompleteQuizAttemptMutationVariables
  >(LearnerQuizCompleteQuizAttempt);

  const handleGenerate = useCallback(() => {
    if (!topic.trim() || loading) {
      return;
    }

    track("quiz_create_button_click", { topic: topic.trim() });

    const token = sessionTokenStore?.getSessionToken();
    if (!token) {
      setError(t("Please sign in to generate a quiz."));
      return;
    }

    socketRef.current?.close();
    setError(null);
    setQuestions([]);
    setSelectedAnswers({});
    setActiveQuestionIndex(0);
    setRevealedByIndex({});
    setAnsweredCorrectByIndex({});
    setGenerationLogStepCount(0);
    setLatestUserLogMessage(null);
    setDidYouKnowFact(pickRandomDidYouKnowFact());
    quizUidRef.current = null;
    quizGenerationGotResultRef.current = false;
    completionSentRef.current = false;
    setAttemptUid(null);
    setLoading(true);

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(
      `${protocol}//${window.location.host}${QUIZ_WRITER_WS_PATH}`,
    );
    socketRef.current = ws;

    let quizPayloadSent = false;

    const sendQuizPayload = () => {
      if (quizPayloadSent || ws.readyState !== WebSocket.OPEN) {
        return;
      }
      quizPayloadSent = true;
      ws.send(
        JSON.stringify({
          topic: topic.trim(),
          age: currentLearner?.age,
        }),
      );
    };

    ws.onopen = () => {
      ws.send(JSON.stringify({ token }));
    };

    ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data as string) as {
          type?: string;
          message?: string;
          questions?: QuizQuestion[];
          quizUid?: string;
        };
        switch (data.type) {
          case "ready":
            sendQuizPayload();
            break;
          case "user_log":
            if (typeof data.message === "string" && data.message.length > 0) {
              setLatestUserLogMessage(data.message);
              setGenerationLogStepCount(prev =>
                Math.min(prev + 1, QUIZ_GENERATION_PROGRESS_CAP),
              );
            }
            break;
          case "result": {
            quizGenerationGotResultRef.current = true;
            const quizUidFromResult =
              typeof data.quizUid === "string" && data.quizUid.length > 0
                ? data.quizUid
                : null;
            quizUidRef.current = quizUidFromResult;
            if (Array.isArray(data.questions)) {
              setQuestions(data.questions);
              track("quiz_generation_success", {
                topic: topic.trim(),
              });
            }
            completionSentRef.current = false;
            void (async () => {
              if (quizUidFromResult) {
                try {
                  const result = await startQuizAttemptMutation({
                    variables: { quizUid: quizUidFromResult },
                  });
                  const uid = result.data?.startQuizAttempt?.uid;
                  if (uid) {
                    setAttemptUid(uid);
                  }
                } catch (error) {
                  OsPlatform.captureError(error, {
                    component: Component.QuizWriter,
                  });
                }
              } else {
                setAttemptUid(null);
              }
              setLoading(false);
              socketRef.current = null;
            })();
            break;
          }
          case "error":
            setError(data.message ?? t("Something went wrong."));
            setLoading(false);
            socketRef.current = null;
            break;
          default:
            break;
        }
      } catch (error) {
        OsPlatform.captureError(error, {
          component: Component.QuizWriter,
        });
        // ignore unparseable messages
      }
    };

    ws.onclose = () => {
      if (socketRef.current !== ws) {
        return;
      }
      socketRef.current = null;
      setLoading(prevLoading => {
        if (prevLoading && !quizGenerationGotResultRef.current) {
          setError(t("Connection closed before the quiz finished."));
        }
        return false;
      });
    };

    ws.onerror = () => {
      setError(t("WebSocket connection error."));
      setLoading(false);
      socketRef.current = null;
    };
  }, [
    topic,
    currentLearner?.age,
    loading,
    sessionTokenStore,
    startQuizAttemptMutation,
    t,
    pickRandomDidYouKnowFact,
    track,
  ]);

  useEffect(() => {
    return () => {
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, []);

  const showResults =
    questions.length > 0 && activeQuestionIndex >= questions.length;

  const handleSelectAnswer = (questionIndex: number, optionIndex: number) => {
    if (revealedByIndex[questionIndex]) {
      return;
    }
    const question = questions[questionIndex];
    const answeredCorrectly =
      question !== undefined && optionIndex === question.answerIndex;
    const nextSelected = { ...selectedAnswers, [questionIndex]: optionIndex };
    const nextRevealed = { ...revealedByIndex, [questionIndex]: true };
    const nextAnsweredCorrect = {
      ...answeredCorrectByIndex,
      [questionIndex]: answeredCorrectly,
    };
    setSelectedAnswers(nextSelected);
    setRevealedByIndex(nextRevealed);
    setAnsweredCorrectByIndex(nextAnsweredCorrect);
    if (attemptUid && questions.length > 0) {
      void updateQuizAttemptMutation({
        variables: {
          attemptUid,
          attempt: quizAttemptPatchFromAnswerMaps(
            nextSelected,
            nextAnsweredCorrect,
          ),
        },
      }).catch(error => {
        OsPlatform.captureError(error, {
          component: Component.QuizWriter,
        });
      });
    }
  };

  const currentRevealed = revealedByIndex[activeQuestionIndex] ?? false;

  const correctSoFar = useMemo(() => {
    if (questions.length === 0) {
      return 0;
    }
    let n = 0;
    for (let i = 0; i < activeQuestionIndex; i++) {
      if (selectedAnswers[i] === questions[i].answerIndex) {
        n++;
      }
    }
    if (
      currentRevealed &&
      selectedAnswers[activeQuestionIndex] ===
        questions[activeQuestionIndex].answerIndex
    ) {
      n++;
    }
    return n;
  }, [activeQuestionIndex, currentRevealed, questions, selectedAnswers]);

  const handleNextQuestion = () => {
    if (activeQuestionIndex < questions.length - 1) {
      setActiveQuestionIndex(prev => prev + 1);
    } else {
      setActiveQuestionIndex(questions.length);
      if (attemptUid && !completionSentRef.current) {
        completionSentRef.current = true;
        void completeQuizAttemptMutation({
          variables: { attemptUid },
        }).catch(error => {
          completionSentRef.current = false;
          OsPlatform.captureError(error, {
            component: Component.QuizWriter,
          });
        });
      }
    }
  };

  const score = questions.reduce((acc, q, i) => {
    return acc + (selectedAnswers[i] === q.answerIndex ? 1 : 0);
  }, 0);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!showResults || questions.length === 0) {
      return;
    }
    track("quiz_results_view", {
      topic: topic.trim(),
      score,
      total_questions: questions.length,
    });
  }, [showResults, questions.length, score, topic, track]);

  const resetSameQuiz = () => {
    track("quiz_try_again_click", { topic: topic.trim() });
    setSelectedAnswers({});
    setRevealedByIndex({});
    setAnsweredCorrectByIndex({});
    setActiveQuestionIndex(0);
    completionSentRef.current = false;
    setAttemptUid(null);
    const qid = quizUidRef.current;
    if (qid) {
      void startQuizAttemptMutation({ variables: { quizUid: qid } })
        .then(result => {
          const uid = result.data?.startQuizAttempt?.uid;
          if (uid) {
            setAttemptUid(uid);
          }
        })
        .catch(error => {
          OsPlatform.captureError(error, {
            component: Component.QuizWriter,
          });
        });
    }
  };

  return (
    <HeroLayout
      header={<Header includeBlob />}
      footer={<Footer />}
      sx={{
        backgroundColor:
          questions.length > 0 && !loading && !showResults
            ? "#F4F0FD"
            : "grey.50",
      }}
    >
      <Box
        sx={{
          maxWidth: 720,
          margin: "0 auto",
          padding: { xs: "16px", sm: "24px" },
          width: "100%",
        }}
      >
        {questions.length === 0 && !loading ? (
          <QuizWriterStartForm
            topic={topic}
            onTopicChange={setTopic}
            onCreateQuiz={handleGenerate}
            loading={loading}
          />
        ) : null}

        {loading && (
          <Box
            sx={{
              backgroundColor: "common.white",
              borderRadius: "20px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
              border: "1px solid",
              borderColor: "grey.100",
              overflow: "hidden",
            }}
          >
            <Box sx={{ padding: { xs: "20px", sm: "28px" } }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  marginBottom: "4px",
                }}
              >
                {t("Quiz Writer")}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", fontSize: "1.5rem" }}
              >
                <Trans
                  t={t}
                  values={{
                    topic: topic.trim() || t("your topic"),
                  }}
                >
                  Creating a quiz about{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: "text.primary" }}
                  >
                    {{ topic }}
                  </Box>
                </Trans>
              </Typography>
            </Box>
            <Box
              sx={{ height: "1px", backgroundColor: "grey.200", opacity: 0.6 }}
            />
            <Box
              sx={{
                padding: { xs: "24px", sm: "32px" },
                textAlign: "center",
              }}
            >
              <Typography
                component="div"
                sx={{
                  fontSize: "2rem",
                  lineHeight: 1,
                  marginBottom: "16px",
                }}
                aria-hidden
              >
                ✨
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  marginBottom: "20px",
                  fontSize: "1.6rem",
                }}
              >
                {t("Preparing your quiz...")}
              </Typography>
              <ProgressBar
                variant="determinate"
                value={
                  (Math.min(
                    generationLogStepCount,
                    QUIZ_GENERATION_PROGRESS_CAP,
                  ) /
                    QUIZ_GENERATION_TOTAL_STEPS) *
                  100
                }
                sx={{
                  height: 10,
                  borderRadius: 999,
                  maxWidth: 440,
                  margin: "0 auto",
                  backgroundColor: "#DCFCE7",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 999,
                    backgroundColor: "#4CAF50",
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  marginTop: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  fontSize: "1.3rem",
                }}
              >
                <Box component="span" sx={{ color: "success.main" }}>
                  ✓
                </Box>{" "}
                {t("{{current}} of {{total}} steps done", {
                  current: Math.min(
                    generationLogStepCount,
                    QUIZ_GENERATION_PROGRESS_CAP,
                  ),
                  total: QUIZ_GENERATION_TOTAL_STEPS,
                })}
              </Typography>
              <Box
                sx={{
                  marginTop: "20px",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  gap: "8px",
                  paddingX: { xs: 0, sm: "16px" },
                }}
              >
                <Typography
                  component="span"
                  sx={{ fontSize: "1.25rem", lineHeight: 1.4 }}
                  aria-hidden
                >
                  🔍
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    textAlign: "left",
                    flex: 1,
                    maxWidth: 400,
                    fontSize: "1.5rem",
                  }}
                >
                  {latestUserLogMessage ?? t("Starting up…")}
                  <Box
                    component="span"
                    sx={{
                      marginLeft: "4px",
                      color: "grey.400",
                      letterSpacing: 3,
                      "@keyframes quizWriterPulse": {
                        "0%, 100%": { opacity: 0.35 },
                        "50%": { opacity: 1 },
                      },
                      animation: "quizWriterPulse 1.2s ease-in-out infinite",
                    }}
                  >
                    ···
                  </Box>
                </Typography>
              </Box>
            </Box>
            <Box sx={{ padding: { xs: "20px", sm: "24px" }, paddingTop: 0 }}>
              <Box
                sx={{
                  borderRadius: "14px",
                  backgroundColor: "#FFFBEB",
                  padding: "16px",
                  textAlign: "left",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "#92400E",
                    letterSpacing: "0.06em",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "8px",
                    fontSize: "1.2rem",
                  }}
                >
                  <span aria-hidden>💡</span> {t("DID YOU KNOW?")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#1C1917",
                    lineHeight: 1.55,
                    fontSize: "1.4rem",
                  }}
                >
                  {didYouKnowFact}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {error && (
          <Box
            sx={{
              padding: "24px",
              backgroundColor: "error.50",
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "error.200",
              marginBottom: "24px",
            }}
          >
            <Typography
              variant="body1"
              sx={{ color: "error.main", fontSize: "1.4rem" }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {questions.length > 0 && !loading && !showResults && (
          <QuestionCard
            key={activeQuestionIndex}
            topic={topic}
            question={questions[activeQuestionIndex]}
            questionIndex={activeQuestionIndex}
            totalQuestions={questions.length}
            correctSoFar={correctSoFar}
            selectedAnswer={selectedAnswers[activeQuestionIndex]}
            showFeedback={currentRevealed}
            onSelectAnswer={handleSelectAnswer}
            onNextQuestion={handleNextQuestion}
          />
        )}

        {showResults && (
          <Box
            sx={{
              overflow: "hidden",
              borderRadius: "20px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
              border: "1px solid",
              borderColor: "grey.100",
              backgroundColor: "common.white",
            }}
          >
            <Box
              sx={{
                background:
                  "linear-gradient(135deg, #F472B6 0%, #A78BFA 50%, #818CF8 100%)",
                padding: { xs: "32px", sm: "40px" },
                textAlign: "center",
              }}
            >
              <Typography
                component="div"
                sx={{ fontSize: "3rem", lineHeight: 1, marginBottom: "16px" }}
                aria-hidden
              >
                🎉
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "common.white",
                  marginBottom: "8px",
                }}
              >
                {score === questions.length
                  ? t("Perfect score!")
                  : score >= questions.length * 0.7
                  ? t("Great job!")
                  : t("Keep it up!")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  fontWeight: 500,
                  fontSize: "1.5rem",
                }}
              >
                {t("You got {{score}} of {{total}} on {{topic}}", {
                  score,
                  total: questions.length,
                  topic: topic.trim() || t("Quiz"),
                })}
              </Typography>
            </Box>
            <Box
              sx={{
                padding: { xs: "20px", sm: "28px" },
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <Button
                onClick={resetSameQuiz}
                variant="outlined"
                size="large"
                sx={{
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1.5rem",
                  padding: "12px 24px",
                  borderColor: "grey.300",
                  color: "text.primary",
                  "&:hover": {
                    borderColor: "grey.400",
                    backgroundColor: "grey.50",
                  },
                }}
              >
                {t("Try again")}
              </Button>
              <Button
                onClick={() => {
                  track("quiz_write_another_click");
                  setTopic("");
                  quizUidRef.current = null;
                  completionSentRef.current = false;
                  setAttemptUid(null);
                  setSelectedAnswers({});
                  setRevealedByIndex({});
                  setAnsweredCorrectByIndex({});
                  setActiveQuestionIndex(0);
                  setQuestions([]);
                  setError(null);
                }}
                variant="contained"
                size="large"
                sx={{
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1.5rem",
                  padding: "12px 24px",
                  backgroundColor: "#7C3AED",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#6D28D9",
                    boxShadow: "none",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {t("Write another quiz")}
                  <Box
                    component="span"
                    sx={{ fontSize: "1.5rem", lineHeight: 1 }}
                  >
                    →
                  </Box>
                </Box>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </HeroLayout>
  );
}

function quizAttemptPatchFromAnswerMaps(
  selectedAnswers: Record<number, number>,
  answeredCorrectByIndex: Record<number, boolean>,
): LearnerQuizUpdateQuizAttemptMutationVariables["attempt"] {
  return {
    selectedAnswers: Object.keys(selectedAnswers).map(k => {
      const questionIndex = Number(k);
      return {
        questionIndex,
        optionIndex: selectedAnswers[questionIndex]!,
      };
    }),
    answeredCorrectByIndex: Object.keys(answeredCorrectByIndex).map(k => {
      const questionIndex = Number(k);
      return {
        questionIndex,
        correct: answeredCorrectByIndex[questionIndex]!,
      };
    }),
  };
}
